"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import clsx from "clsx";

import { Button } from "../core/Button";
import ChevronLeft16 from "@ui/icons/16x16/chevron-left-16.svg";
import ChevronRight16 from "@ui/icons/16x16/chevron-right-16.svg";
import { CornerMarkerDot } from "../core/CornerMarkerDot";
import { useHasFinePointer } from "@/app/_utilities/useHasFinePointer";

// Scroll by one card width (25% of container on lg screens where nav buttons are visible)
const SCROLL_FRACTION = 0.25;

interface ScrollButtonProps {
  direction: "prev" | "next";
  onClick: () => void;
  disabled: boolean;
  ariaLabel: string;
}

const ScrollButton = ({ direction, onClick, disabled, ariaLabel }: ScrollButtonProps) => {
  const Icon = direction === "prev" ? ChevronLeft16 : ChevronRight16;

  return (
    <div className='tw-pointer-events-auto'>
      <Button
        aria-label={ariaLabel}
        className={clsx(
          "tw-flex tw-size-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-p-0",
          disabled && "tw-opacity-30 tw-cursor-not-allowed",
        )}
        disabled={disabled}
        icon={<Icon />}
        onClick={onClick}
        variant='elevated'
      />
    </div>
  );
};

export interface CardItem {
  id: string;
  content: ReactNode;
}

interface ScrollablePricingCardsGridProps {
  /** Array of card items to render */
  items: CardItem[];
  /** Aria labels for scroll buttons */
  scrollLabels: {
    previousSlide: string;
    nextSlide: string;
  };
  /** Card width classes for responsive sizing */
  cardWidthClassName?: string;
  /** Vertical position for navigation buttons (in rem) */
  buttonVerticalPositionRem?: number;
  /** Additional className for items */
  itemClassName?: string;
  /** Whether to show bottom border on items */
  showBottomBorder?: boolean;
  /** Corner marker background color */
  markerBackgroundColor?: string;
}

/**
 * Reusable horizontal scrollable pricing cards grid with navigation buttons.
 * Shows prev/next buttons on hover for desktop users.
 */
export const ScrollablePricingCardsGrid = ({
  items,
  scrollLabels,
  cardWidthClassName = "tw-w-[calc(100%-2rem)] md:tw-w-[calc(50%-1rem)] lg:tw-w-[23%]",
  buttonVerticalPositionRem = 6.25, // Default: midpoint of 12.5rem card header
  itemClassName,
  showBottomBorder = true,
  markerBackgroundColor = "white",
}: ScrollablePricingCardsGridProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);
  const hasFinePointer = useHasFinePointer();

  // Check if we need to show the previous or next button
  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const maxScroll = container.scrollWidth - container.clientWidth;
    const currentScroll = container.scrollLeft;

    setCanScrollPrev(currentScroll > 10);
    setCanScrollNext(currentScroll < maxScroll - 10);
  }, []);

  // Scroll in a given direction
  const scrollToDirection = useCallback((direction: "prev" | "next") => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth * SCROLL_FRACTION;
    const currentScroll = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;

    const targetScroll =
      direction === "prev"
        ? Math.max(0, currentScroll - scrollAmount)
        : Math.min(maxScroll, currentScroll + scrollAmount);

    container.scrollTo({
      left: targetScroll,
      behavior: "smooth",
    });
  }, []);

  // Track scroll state
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      updateScrollState();
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [updateScrollState]);

  // Initialize scroll state on mount and when items change
  useEffect(() => {
    updateScrollState();
  }, [updateScrollState, items.length]);

  return (
    <div className='tw-relative tw-group/grid'>
      <div
        ref={scrollContainerRef}
        className='tw-overflow-x-auto tw-overflow-y-hidden tw-scrollbar-none tw-py-2 -tw-my-2'
      >
        <div className='tw-flex tw-flex-nowrap tw-gap-0'>
          {items.map((item, index) => {
            const isLast = index === items.length - 1;

            return (
              <div
                key={item.id}
                className={clsx(
                  "tw-relative tw-flex-shrink-0 tw-px-4 tw-pt-4",
                  showBottomBorder && "tw-border-b tw-border-black/5",
                  cardWidthClassName,
                  itemClassName,
                )}
              >
                {item.content}

                {!isLast && (
                  <div className='tw-absolute tw-right-0 tw-top-0 tw-bottom-0 tw-w-[1px] tw-z-10 tw-bg-black/5'>
                    <CornerMarkerDot backgroundColor={markerBackgroundColor} position='topCentered' />
                    <CornerMarkerDot backgroundColor={markerBackgroundColor} position='bottomCentered' />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation buttons - visible on hover (lg+ only) */}
      {hasFinePointer && (
        <div
          className='tw-pointer-events-none tw-absolute -tw-left-[calc(2.75rem+1rem+var(--page-section-px,0px))] -tw-right-[calc(2.75rem+1rem+var(--page-section-px,0px))] tw-hidden tw-justify-between tw-opacity-0 tw-transition-opacity tw-duration-200 group-hover/grid:tw-opacity-100 xl:tw-flex'
          style={{ top: `${buttonVerticalPositionRem}rem` }}
        >
          <ScrollButton
            direction='prev'
            onClick={() => scrollToDirection("prev")}
            disabled={!canScrollPrev}
            ariaLabel={scrollLabels.previousSlide}
          />
          <ScrollButton
            direction='next'
            onClick={() => scrollToDirection("next")}
            disabled={!canScrollNext}
            ariaLabel={scrollLabels.nextSlide}
          />
        </div>
      )}
    </div>
  );
};
